import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ApiError,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  StatusChip,
  fmtMoney,
} from '@smena/ui';
import { Modal } from '@/components/Modal';
import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { StatTile } from '@/components/StatTile';
import { fetchAccounts } from '@/features/accounts/api';
import {
  PAYMENT_KIND_LABELS,
  createPayment,
  deletePayment,
  fetchPayments,
  fetchRevenue,
  type PaymentKind,
} from '@/features/payments/api';

export function PaymentsPage() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const paymentsQ = useQuery({ queryKey: ['payments'], queryFn: () => fetchPayments() });
  const revenueQ = useQuery({ queryKey: ['revenue'], queryFn: fetchRevenue });

  const delMut = useMutation({
    mutationFn: deletePayment,
    onSuccess: () => {
      toast.success('Платёж удалён');
      void qc.invalidateQueries({ queryKey: ['payments'] });
      void qc.invalidateQueries({ queryKey: ['revenue'] });
      void qc.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось удалить'),
  });

  const r = revenueQ.data;

  return (
    <>
      <PageHeader
        title="Платежи"
        subtitle="Ручной реестр наших оплат до автоматического биллинга. Факт и расчётный план - рядом."
        actions={<Button onClick={() => setAdding(true)}>Внести оплату</Button>}
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile label="Оплачено сегодня" value={fmtMoney(r?.paidToday ?? 0)} />
        <StatTile label="Этот месяц" value={fmtMoney(r?.paidThisMonth ?? 0)} hint={`за 30 дней: ${fmtMoney(r?.paidLast30 ?? 0)}`} />
        <StatTile
          label="Расчётный MRR"
          value={fmtMoney(r?.expectedMrr ?? 0)}
          hint="Σ(база + за объект × объекты) по боевым аккаунтам"
        />
        <StatTile
          label="Не оплатили"
          value={r?.unpaidAccounts.length ?? 0}
          tone={(r?.unpaidAccounts.length ?? 0) > 0 ? 'warn' : 'default'}
          hint="аккаунты с тарифом, но без оплаты за 35 дней"
        />
      </div>

      {r?.unpaidAccounts.length ? (
        <Card className="mb-4 border-amber/40">
          <CardHeader>
            <CardTitle>Ждут оплаты</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm">
            {r.unpaidAccounts.map((a) => (
              <div key={a.accountId} className="flex items-center justify-between gap-3">
                <Link to={`/accounts/${a.accountId}`} className="text-accent-fg hover:underline">
                  {a.name}
                </Link>
                <span className="text-xs text-muted">
                  {fmtMoney(a.expectedRub)}/мес · последняя оплата: {a.lastPaidAt ?? 'никогда'}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      <QueryState
        isLoading={paymentsQ.isLoading}
        error={paymentsQ.error}
        isEmpty={paymentsQ.data?.length === 0}
        emptyText="Оплат пока нет"
      >
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-line text-left text-xs uppercase tracking-wider text-muted">
              <tr>
                <th className="px-4 py-2 font-medium">Дата</th>
                <th className="px-4 py-2 font-medium">Аккаунт</th>
                <th className="px-4 py-2 font-medium">Тип</th>
                <th className="px-4 py-2 text-right font-medium">Сумма</th>
                <th className="px-4 py-2 font-medium">Период</th>
                <th className="px-4 py-2 font-medium">Способ</th>
                <th className="px-4 py-2 font-medium">Заметка</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {paymentsQ.data?.map((p) => (
                <tr key={p.id} className="border-b border-line/60 last:border-0 hover:bg-panel2">
                  <td className="whitespace-nowrap px-4 py-2 text-xs text-muted">
                    {new Date(p.paidAt).toLocaleDateString('ru-RU')}
                  </td>
                  <td className="px-4 py-2">
                    <Link to={`/accounts/${p.accountId}`} className="text-accent-fg hover:underline">
                      {p.accountName ?? p.accountSlug ?? p.accountId}
                    </Link>
                  </td>
                  <td className="px-4 py-2">
                    <StatusChip tone={p.kind === 'subscription' ? 'accent' : 'muted'}>
                      {PAYMENT_KIND_LABELS[p.kind] ?? p.kind}
                    </StatusChip>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmtMoney(p.amountRub)}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-xs text-muted">
                    {p.periodFrom || p.periodTo ? `${p.periodFrom ?? '…'} → ${p.periodTo ?? '…'}` : '-'}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted">{p.method ?? '-'}</td>
                  <td className="max-w-48 truncate px-4 py-2 text-xs text-muted">{p.note ?? '-'}</td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={delMut.isPending}
                      onClick={() => {
                        if (confirm(`Удалить оплату ${fmtMoney(p.amountRub)}?`)) delMut.mutate(p.id);
                      }}
                    >
                      Удалить
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </QueryState>

      {adding ? <AddPaymentModal onClose={() => setAdding(false)} /> : null}
    </>
  );
}

function AddPaymentModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const accountsQ = useQuery({ queryKey: ['accounts'], queryFn: fetchAccounts, staleTime: 60_000 });

  const [accountId, setAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [kind, setKind] = useState<PaymentKind>('subscription');
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [method, setMethod] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: createPayment,
    onSuccess: () => {
      toast.success('Оплата внесена');
      void qc.invalidateQueries({ queryKey: ['payments'] });
      void qc.invalidateQueries({ queryKey: ['revenue'] });
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      onClose();
    },
  });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await mut.mutateAsync({
        accountId,
        amountRub: Number(amount),
        kind,
        periodFrom: periodFrom || null,
        periodTo: periodTo || null,
        method: method || null,
        note: note || null,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось внести оплату');
    }
  }

  return (
    <Modal title="Новая оплата" onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="p-account">Аккаунт</Label>
          <select
            id="p-account"
            required
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            className="h-10 rounded-md border border-line-control bg-input px-2 text-sm text-text"
          >
            <option value="">выберите аккаунт</option>
            {accountsQ.data?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.slug})
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="p-amount">Сумма, ₽</Label>
            <Input
              id="p-amount"
              inputMode="numeric"
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/\D/g, ''))}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="p-kind">Тип</Label>
            <select
              id="p-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as PaymentKind)}
              className="h-10 rounded-md border border-line-control bg-input px-2 text-sm text-text"
            >
              {Object.entries(PAYMENT_KIND_LABELS).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="p-from">Период с</Label>
            <Input id="p-from" type="date" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="p-to">Период по</Label>
            <Input id="p-to" type="date" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="p-method">Способ</Label>
          <Input
            id="p-method"
            placeholder="перевод на счёт / карта / нал"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="p-note">Заметка</Label>
          <Input id="p-note" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        {error ? (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        ) : null}
        <div className="mt-2 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? 'Вносим…' : 'Внести'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
